import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { communityServicesAPI } from "../../../services/api";

const ProgramDetailsModal = ({ isOpen, onClose, programId, onEdit }) => {
  const [volunteerDetails, setVolunteerDetails] = useState([]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  const { data: programData, isLoading } = useQuery(
    ["community-services", "program", programId],
    async () => {
      const res = await communityServicesAPI.getProgramById(programId);
      const raw = res?.data?.data?.program || res?.data?.data || res?.data;
      return raw;
    },
    { enabled: Boolean(isOpen && programId) }
  );

  console.log("DATA GET PROG BY ID: ", programData)

  useEffect(() => {
    const volunteers = Array.isArray(programData?.volunteers)
      ? programData.volunteers
      : [];
    setVolunteerDetails(volunteers);
  }, [programData]);

  if (!isOpen) return null;

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "-";
    const date = new Date(dateTimeString);
    const month = date.toLocaleString("en-US", { month: "short" });
    const day = date.getDate();
    const year = date.getFullYear();
    const time = date.toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${month} ${day}, ${year}, ${time}`;
  };

  const getAttendanceStatus = (volunteer) => {
    return volunteer?.attendance?.status || null;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Program Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18 18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Program Name
                </h3>
                <p className="text-lg font-semibold text-gray-900">
                  {programData?.name || (isLoading ? "Loading..." : "-")}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Date & Time
                </h3>
                <p className="text-base text-gray-900">
                  {programData?.start_date
                    ? formatDateTime(programData.start_date)
                    : "-"}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Location
                </h3>
                <p className="text-base text-gray-900">
                  {programData?.location || "-"}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Description
                </h3>
                <p className="text-base text-gray-900">
                  {programData?.description || "-"}
                </p>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Organizer - static */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Organizer
                </h3>
                <p className="text-base text-gray-900">Organization Name</p>
              </div>

              {/* Volunteers */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  Assigned Volunteers
                </h3>
                <p className="text-base text-gray-900 mb-4">
                  Found {volunteerDetails.length} assigned volunteer(s)
                </p>

                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {volunteerDetails.length > 0 ? (
                    volunteerDetails.map((volunteer, idx) => {
                      const attendanceStatus = getAttendanceStatus(volunteer);
                      
                      return (
                        <div
                          key={idx}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-start gap-3">
                            <div className="flex-shrink-0">
                              <div className="w-8 h-8 rounded-full bg-blue-500 border-2 border-white flex items-center justify-center text-[10px] p-2 font-medium text-white">
                                {volunteer.initials ||
                                  volunteer.full_name
                                    ?.split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .substring(0, 2)
                                    .toUpperCase()}
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                {volunteer.full_name || volunteer.name}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-800">
                                  {volunteer.barangay_id_no || "-"}
                                </span>
                                <span>•</span>
                                
                                {/* Attendance Status - Replaced volunteer status */}
                                {attendanceStatus ? (
                                  <span className={`px-2 py-1 rounded-full ${
                                    attendanceStatus === "present" 
                                      ? "bg-blue-100 text-blue-800" 
                                      : attendanceStatus === "absent" 
                                      ? "bg-red-100 text-red-800" 
                                      : "bg-gray-100 text-gray-800"
                                  }`}>
                                    {attendanceStatus}
                                  </span>
                                ) : (
                                  <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-500">
                                    no attendance
                                  </span>
                                )}
                                
                                <span>•</span>
                                <span className="capitalize">{volunteer.role || "volunteer"}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-500 text-sm">
                      No assigned volunteers yet.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex justify-end">
          <button
            onClick={() => onEdit?.(programId)}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Edit Program Details
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProgramDetailsModal;